import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Phone, Calendar, HelpCircle, Smartphone } from 'lucide-react';

// FAQ Schema component for SEO
const FAQSchema = ({ sections }) => {
  const allQuestions = sections.flatMap(section => section.questions);
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allQuestions.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a.replace(/<[^>]*>/g, '') // Strip HTML tags for schema
      }
    }))
  };

  return (
    <script 
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
    />
  );
};

const FAQPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      metaTitle: "FAQ — Questions Fréquentes | MTL Taxi Montréal",
      metaDesc: "Toutes les réponses à vos questions sur MTL Taxi Montréal : réservation, tarifs, aéroport YUL, paiement, animaux, objets perdus et plus. Service 24h/24 — 438-600-4440.",
      title: "Questions Fréquentes — MTL Taxi Montréal",
      intro: "Trouvez rapidement les réponses à vos questions. Vous ne trouvez pas ce que vous cherchez ?",
      introCall: "Appelez-nous au",
      introAvailable: "— disponible 24h/24.",
      
      sections: [
        {
          title: "Réservation",
          questions: [
            {
              q: "Comment puis-je réserver un taxi ?",
              a: "Trois façons de réserver avec MTL Taxi : appelez le 438-600-4440, utilisez notre portail de réservation en ligne, ou téléchargez notre application mobile disponible sur App Store et Google Play. La réservation à l'avance est recommandée pour les navettes aéroport et les déplacements tôt le matin."
            },
            {
              q: "Peut-on réserver à l'avance ?",
              a: "Oui — et c'est fortement recommandé pour les départs tôt le matin, les vols, ou tout déplacement important. Précisez votre adresse, l'heure souhaitée et le nombre de passagers via téléphone, notre portail en ligne ou l'application."
            },
            {
              q: "Combien de temps à l'avance dois-je réserver pour l'aéroport ?",
              a: "Idéalement 24 heures à l'avance, mais nous acceptons les réservations de dernière minute selon la disponibilité. Pour les vols tôt le matin, réservez la veille via l'application ou en appelant le 438-600-4440."
            },
            {
              q: "Puis-je réserver un taxi pour quelqu'un d'autre ?",
              a: "Absolument. Par téléphone, via le portail de réservation ou l'application — donnez l'adresse de prise en charge, l'heure et le nom de la personne, notre chauffeur s'y rendra directement."
            },
            {
              q: "Y a-t-il un délai d'attente minimal après la réservation ?",
              a: "Pour une course immédiate, appelez le 438-600-4440 ou commandez via l'application — un chauffeur disponible sera dépêché le plus rapidement possible selon votre secteur."
            },
            {
              q: "Comment fonctionne l'application MTL Taxi ?",
              a: "Téléchargez l'application sur App Store ou Google Play, créez votre compte, et commandez, réservez ou suivez votre taxi en temps réel directement depuis votre téléphone."
            }
          ]
        },
        {
          title: "Tarifs & Paiement",
          questions: [
            {
              q: "Quels modes de paiement acceptez-vous ?",
              a: "Comptant, carte de débit Interac, Visa, Mastercard et American Express. Les comptes corporatifs bénéficient d'une facturation mensuelle centralisée.",
              link: { text: "En savoir plus sur les comptes corporatifs", url: "/services" }
            },
            {
              q: "Comment sont calculés les tarifs ?",
              a: "Les tarifs sont fixés par la Commission des transports du Québec (CTQ) : prise en charge à 4,10 $, puis 2,05 $ par kilomètre de jour. Le trajet centre-ville ↔ aéroport YUL est à tarif fixe : 49,45 $ le jour et 56,70 $ la nuit.",
              link: { text: "Voir tous les tarifs", url: "/fares" }
            },
            {
              q: "Y a-t-il des frais supplémentaires la nuit ?",
              a: "Oui — le tarif de nuit s'applique entre 23h et 5h du matin. La prise en charge passe à 4,70 $ et le kilométrage à 2,35 $ / km.",
              link: { text: "Voir le tableau complet des tarifs", url: "/fares" }
            },
            {
              q: "Le prix est-il le même qu'Uber ?",
              a: "Les tarifs de taxi sont réglementés par le gouvernement du Québec — il n'y a pas de tarification dynamique. Contrairement aux plateformes de covoiturage, le prix ne monte pas en heure de pointe, sous la pluie ou lors d'événements spéciaux."
            },
            {
              q: "Reçoit-on un reçu ?",
              a: "Oui, un reçu est remis à la fin de chaque course. Les comptes corporatifs reçoivent des rapports de dépenses détaillés chaque mois. L'application conserve aussi l'historique de vos courses."
            },
            {
              q: "Y a-t-il un supplément pour le minivan ?",
              a: "Oui, un supplément de 10 $ s'applique pour les courses en minivan 6 passagers.",
              link: { text: "En savoir plus sur le minivan", url: "/services" }
            }
          ]
        },
        {
          title: "Aéroport",
          questions: [
            {
              q: "Offrez-vous un service de navette aéroport ?",
              a: "Oui — MTL Taxi assure les transferts vers et depuis l'aéroport Montréal-Trudeau (YUL) 24h/24, 365 jours par an. Réservez par téléphone, via le portail en ligne ou l'application.",
              link: { text: "Voir la page aéroport", url: "/airport" }
            },
            {
              q: "Quel est le tarif fixe pour l'aéroport ?",
              a: "Le tarif fixe centre-ville ↔ YUL est de 49,45 $ de jour (5h–23h) et 56,70 $ de nuit (23h–5h). Pour les adresses hors centre-ville, le taximètre s'applique.",
              link: { text: "Voir les estimations par zone", url: "/fares" }
            },
            {
              q: "Que se passe-t-il si mon vol est retardé ?",
              a: "Nous suivons votre vol. Si votre avion arrive en retard, votre chauffeur ajuste l'heure de prise en charge automatiquement — sans frais supplémentaires."
            },
            {
              q: "Où le chauffeur m'attend-il à l'aéroport ?",
              a: "Votre chauffeur vous attend à la zone des arrivées selon vos instructions. Précisez votre numéro de vol à la réservation — via téléphone, portail ou application."
            },
            {
              q: "Proposez-vous le minivan pour l'aéroport avec des bagages volumineux ?",
              a: "Oui — le minivan 6 passagers est idéal pour les familles ou groupes avec beaucoup de bagages. Mentionnez-le lors de votre réservation.",
              link: { text: "En savoir plus", url: "/services" }
            }
          ]
        },
        {
          title: "Véhicules & Passagers",
          questions: [
            {
              q: "Combien de passagers peuvent monter dans un taxi ?",
              a: "La berline standard accueille jusqu'à 4 passagers. Le minivan accueille jusqu'à 6 passagers avec bagages.",
              link: { text: "En savoir plus sur le minivan", url: "/services" }
            },
            {
              q: "Proposez-vous des véhicules pour personnes à mobilité réduite ?",
              a: "Oui — MTL Taxi dispose de véhicules adaptés équipés de rampes d'accès pour fauteuils roulants. Nos chauffeurs sont formés pour accompagner les personnes à mobilité réduite avec respect et professionnalisme. La réservation à l'avance est recommandée — par téléphone au 438-600-4440 ou via l'application.",
              link: { text: "En savoir plus sur le taxi accessible", url: "/services" }
            },
            {
              q: "Les animaux de compagnie sont-ils acceptés ?",
              a: "Les animaux de service (chiens-guides) sont acceptés en tout temps. Pour les autres animaux de compagnie, mentionnez-le à la réservation — le chauffeur confirmera la disponibilité selon le véhicule assigné."
            },
            {
              q: "Les enfants en bas âge sont-ils acceptés ?",
              a: "Oui. Si vous avez besoin d'un siège d'auto pour enfant, mentionnez-le lors de la réservation par téléphone ou via l'application et nous ferons notre possible pour en fournir un."
            }
          ]
        },
        {
          title: "Services Express",
          questions: [
            {
              q: "Offrez-vous d'autres services que le transport ?",
              a: "Oui — MTL Taxi propose trois services express : survoltage de batterie (25 $), déverrouillage de porte (30 $) et livraison de colis (6,50 $ + taximètre). Disponibles 24h/24 sur simple appel.",
              link: { text: "Voir tous les services", url: "/services" }
            },
            {
              q: "Le survoltage est-il disponible la nuit ?",
              a: "Oui — tous nos services express sont disponibles 24h/24. Appelez le 438-600-4440 à tout moment."
            },
            {
              q: "Livrez-vous partout à Montréal ?",
              a: "Nous livrons sur l'ensemble du Grand Montréal. Le tarif est calculé au taximètre à partir de 6,50 $. Appelez pour une estimation selon la distance."
            }
          ]
        },
        {
          title: "Objets perdus & Divers",
          questions: [
            {
              q: "J'ai oublié un objet dans le taxi — que faire ?",
              a: "Appelez immédiatement le 438-600-4440 en précisant l'heure approximative de votre course, votre adresse de départ et de destination. Nous joignons le chauffeur concerné et faisons notre possible pour récupérer votre objet.",
              link: { text: "Page Contact", url: "/contact" }
            },
            {
              q: "Le service est-il disponible les jours fériés ?",
              a: "Oui — MTL Taxi est disponible 365 jours par an, incluant tous les jours fériés : Noël, Jour de l'An, Saint-Jean-Baptiste, Fête du Canada, etc."
            },
            {
              q: "Servez-vous les banlieues de Montréal ?",
              a: "Oui — nous desservons l'ensemble du Grand Montréal incluant Laval, Longueuil, Brossard, Saint-Lambert et les environs.",
              link: { text: "Voir toutes les zones desservies", url: "/zones" }
            },
            {
              q: "Proposez-vous des trajets vers d'autres villes ?",
              a: "Pour les trajets longue distance hors Grand Montréal, appelez le 438-600-4440 pour une estimation personnalisée."
            },
            {
              q: "Comment devenir chauffeur chez MTL Taxi ?",
              a: "Contactez-nous au 438-600-4440 pour toute demande de partenariat chauffeur.",
              link: { text: "Page Contact", url: "/contact" }
            }
          ]
        }
      ],
      
      ctaTitle: "Vous avez d'autres questions ?",
      ctaText: "Notre équipe est disponible 24h/24, 7j/7 pour répondre à toutes vos questions.",
      callBtn: "Appeler 438-600-4440",
      bookBtn: "Portail de réservation en ligne",
      appStore: "Télécharger l'application — App Store",
      googlePlay: "Télécharger l'application — Google Play",
      
      footerHome: "← Accueil",
      footerServices: "Services",
      footerAirport: "Aéroport YUL",
      footerFares: "Tarifs",
      footerZones: "Zones desservies",
      footerContact: "Contact"
    },
    en: {
      metaTitle: "FAQ — Frequently Asked Questions | MTL Taxi Montreal",
      metaDesc: "All answers to your questions about MTL Taxi Montreal: booking, rates, YUL airport, payment, pets, lost items and more. 24/7 service — 438-600-4440.",
      title: "Frequently Asked Questions — MTL Taxi Montreal",
      intro: "Find answers to your questions quickly. Can't find what you're looking for?",
      introCall: "Call us at",
      introAvailable: "— available 24/7.",
      
      sections: [
        {
          title: "Booking",
          questions: [
            {
              q: "How can I book a taxi?",
              a: "Three ways to book with MTL Taxi: call 438-600-4440, use our online booking portal, or download our mobile app available on App Store and Google Play. Advance booking is recommended for airport shuttles and early morning trips."
            },
            {
              q: "Can I book in advance?",
              a: "Yes — and it's highly recommended for early morning departures, flights, or any important trip. Specify your address, desired time and number of passengers via phone, our online portal or the app."
            },
            {
              q: "How far in advance should I book for the airport?",
              a: "Ideally 24 hours in advance, but we accept last-minute bookings based on availability. For early morning flights, book the night before via the app or by calling 438-600-4440."
            },
            {
              q: "Can I book a taxi for someone else?",
              a: "Absolutely. By phone, via the booking portal or the app — provide the pickup address, time and person's name, our driver will go directly there."
            },
            {
              q: "Is there a minimum wait time after booking?",
              a: "For an immediate ride, call 438-600-4440 or order via the app — an available driver will be dispatched as quickly as possible based on your area."
            },
            {
              q: "How does the MTL Taxi app work?",
              a: "Download the app on App Store or Google Play, create your account, and order, book or track your taxi in real-time directly from your phone."
            }
          ]
        },
        {
          title: "Rates & Payment",
          questions: [
            {
              q: "What payment methods do you accept?",
              a: "Cash, Interac debit card, Visa, Mastercard and American Express. Corporate accounts benefit from centralized monthly billing.",
              link: { text: "Learn more about corporate accounts", url: "/services" }
            },
            {
              q: "How are rates calculated?",
              a: "Rates are set by the Commission des transports du Québec (CTQ): pickup fee of $4.10, then $2.05 per kilometer during the day. The downtown ↔ YUL airport trip is a fixed rate: $49.45 day and $56.70 night.",
              link: { text: "View all rates", url: "/fares" }
            },
            {
              q: "Are there extra charges at night?",
              a: "Yes — the night rate applies between 11pm and 5am. The pickup fee goes to $4.70 and the per-kilometer rate to $2.35/km.",
              link: { text: "View the complete rate table", url: "/fares" }
            },
            {
              q: "Is the price the same as Uber?",
              a: "Taxi rates are regulated by the Quebec government — there is no dynamic pricing. Unlike rideshare platforms, the price doesn't go up during rush hour, in the rain or during special events."
            },
            {
              q: "Do I get a receipt?",
              a: "Yes, a receipt is provided at the end of each trip. Corporate accounts receive detailed expense reports each month. The app also keeps your trip history."
            },
            {
              q: "Is there a supplement for the minivan?",
              a: "Yes, a $10 supplement applies for rides in the 6-passenger minivan.",
              link: { text: "Learn more about the minivan", url: "/services" }
            }
          ]
        },
        {
          title: "Airport",
          questions: [
            {
              q: "Do you offer airport shuttle service?",
              a: "Yes — MTL Taxi provides transfers to and from Montreal-Trudeau Airport (YUL) 24/7, 365 days a year. Book by phone, via the online portal or the app.",
              link: { text: "View the airport page", url: "/airport" }
            },
            {
              q: "What is the fixed rate for the airport?",
              a: "The fixed rate downtown ↔ YUL is $49.45 day (5am–11pm) and $56.70 night (11pm–5am). For addresses outside downtown, the meter applies.",
              link: { text: "View zone estimates", url: "/fares" }
            },
            {
              q: "What happens if my flight is delayed?",
              a: "We track your flight. If your plane arrives late, your driver automatically adjusts the pickup time — at no extra charge."
            },
            {
              q: "Where does the driver wait for me at the airport?",
              a: "Your driver waits in the arrivals area according to your instructions. Specify your flight number when booking — via phone, portal or app."
            },
            {
              q: "Do you offer minivan for the airport with bulky luggage?",
              a: "Yes — the 6-passenger minivan is ideal for families or groups with lots of luggage. Mention it when booking.",
              link: { text: "Learn more", url: "/services" }
            }
          ]
        },
        {
          title: "Vehicles & Passengers",
          questions: [
            {
              q: "How many passengers can ride in a taxi?",
              a: "The standard sedan accommodates up to 4 passengers. The minivan accommodates up to 6 passengers with luggage.",
              link: { text: "Learn more about the minivan", url: "/services" }
            },
            {
              q: "Do you offer vehicles for people with reduced mobility?",
              a: "Yes — MTL Taxi has adapted vehicles equipped with wheelchair access ramps. Our drivers are trained to assist people with reduced mobility with respect and professionalism. Advance booking is recommended — by phone at 438-600-4440 or via the app.",
              link: { text: "Learn more about accessible taxi", url: "/services" }
            },
            {
              q: "Are pets allowed?",
              a: "Service animals (guide dogs) are accepted at all times. For other pets, mention it when booking — the driver will confirm availability based on the assigned vehicle."
            },
            {
              q: "Are infants accepted?",
              a: "Yes. If you need a child car seat, mention it when booking by phone or via the app and we will do our best to provide one."
            }
          ]
        },
        {
          title: "Express Services",
          questions: [
            {
              q: "Do you offer services other than transportation?",
              a: "Yes — MTL Taxi offers three express services: battery boost ($25), door unlock ($30) and parcel delivery ($6.50 + meter). Available 24/7 on a simple call.",
              link: { text: "View all services", url: "/services" }
            },
            {
              q: "Is battery boost available at night?",
              a: "Yes — all our express services are available 24/7. Call 438-600-4440 anytime."
            },
            {
              q: "Do you deliver everywhere in Montreal?",
              a: "We deliver throughout Greater Montreal. The rate is calculated by meter starting at $6.50. Call for an estimate based on distance."
            }
          ]
        },
        {
          title: "Lost Items & Misc",
          questions: [
            {
              q: "I forgot an item in the taxi — what should I do?",
              a: "Call 438-600-4440 immediately specifying the approximate time of your trip, your departure and destination address. We contact the driver involved and do our best to retrieve your item.",
              link: { text: "Contact page", url: "/contact" }
            },
            {
              q: "Is the service available on holidays?",
              a: "Yes — MTL Taxi is available 365 days a year, including all holidays: Christmas, New Year's Day, Saint-Jean-Baptiste, Canada Day, etc."
            },
            {
              q: "Do you serve Montreal suburbs?",
              a: "Yes — we serve all of Greater Montreal including Laval, Longueuil, Brossard, Saint-Lambert and surrounding areas.",
              link: { text: "View all service areas", url: "/zones" }
            },
            {
              q: "Do you offer trips to other cities?",
              a: "For long-distance trips outside Greater Montreal, call 438-600-4440 for a personalized estimate."
            },
            {
              q: "How can I become a driver at MTL Taxi?",
              a: "Contact us at 438-600-4440 for any driver partnership inquiries.",
              link: { text: "Contact page", url: "/contact" }
            }
          ]
        }
      ],
      
      ctaTitle: "Have more questions?",
      ctaText: "Our team is available 24/7 to answer all your questions.",
      callBtn: "Call 438-600-4440",
      bookBtn: "Online booking portal",
      appStore: "Download the app — App Store",
      googlePlay: "Download the app — Google Play",
      
      footerHome: "← Home",
      footerServices: "Services",
      footerAirport: "YUL Airport",
      footerFares: "Rates",
      footerZones: "Service Areas",
      footerContact: "Contact"
    }
  };

  const t = content[language] || content.fr;

  return (
    <div className="min-h-screen pt-20" data-testid="faq-page">
      <SEO 
        title={t.metaTitle}
        description={t.metaDesc}
        canonical={`https://mtl.taxi/${language === 'fr' ? 'fr/faq' : 'en/faq'}`}
      />
      <FAQSchema sections={t.sections} />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl mb-6 tracking-tight leading-tight">
            {t.title}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t.intro} {t.introCall} <a href={siteConfig.phoneTel} className="font-bold underline">438-600-4440</a> {t.introAvailable}
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <div className="py-16 md:py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {t.sections.map((section, sectionIndex) => (
            <section key={sectionIndex} className="mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">
                {section.title}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {section.questions.map((faq, faqIndex) => (
                  <AccordionItem 
                    key={faqIndex} 
                    value={`section-${sectionIndex}-item-${faqIndex}`}
                    className="border rounded-lg px-6 bg-card shadow-sm"
                  >
                    <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4">
                      <p className="mb-2">{faq.a}</p>
                      {faq.link && (
                        <Link 
                          to={getRoute(faq.link.url)} 
                          className="text-primary font-semibold hover:underline"
                        >
                          → {faq.link.text}
                        </Link>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-accent-foreground mb-4">
            {t.ctaTitle}
          </h2>
          <p className="text-accent-foreground/80 text-lg mb-8">
            {t.ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Button
              asChild
              size="lg"
              className="bg-primary text-white hover:bg-primary/90 font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.phoneTel}>
                <Phone className="w-5 h-5 mr-2" />
                {t.callBtn}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white font-bold text-lg h-14 px-8"
            >
              <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                <Calendar className="w-5 h-5 mr-2" />
                {t.bookBtn}
              </a>
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="text-accent-foreground/70 hover:text-accent-foreground font-medium"
            >
              <a href="#" target="_blank" rel="noopener noreferrer">
                <Smartphone className="w-5 h-5 mr-2" />
                {t.appStore}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="ghost"
              className="text-accent-foreground/70 hover:text-accent-foreground font-medium"
            >
              <a href="#" target="_blank" rel="noopener noreferrer">
                <Smartphone className="w-5 h-5 mr-2" />
                {t.googlePlay}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Links */}
      <section className="py-8 bg-muted/50 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
            <Link to={getRoute('/')} className="text-muted-foreground hover:text-primary">{t.footerHome}</Link>
            <Link to={getRoute('/services')} className="text-muted-foreground hover:text-primary">{t.footerServices}</Link>
            <Link to={getRoute('/airport')} className="text-muted-foreground hover:text-primary">{t.footerAirport}</Link>
            <Link to={getRoute('/fares')} className="text-muted-foreground hover:text-primary">{t.footerFares}</Link>
            <Link to={getRoute('/zones')} className="text-muted-foreground hover:text-primary">{t.footerZones}</Link>
            <Link to={getRoute('/contact')} className="text-muted-foreground hover:text-primary">{t.footerContact}</Link>
          </nav>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
